<template>
  <div class="reservoir-dual-panel">
    <!-- 面板1: 左下角时间序列折线图 -->
    <div class="ts-panel">
      <div class="panel-header">
        <div>
          <div class="panel-kicker">实时监测</div>
          <h2 class="panel-title">{{ oneTitle }}</h2>
          <p class="panel-subtitle">24小时时间序列 · 15分钟间隔</p>
        </div>
        <button class="panel-close" @click="closeClick">×</button>
      </div>

      <div class="metrics-row">
        <div class="metric-card" v-for="(item, idx) in currentMetrics" :key="'cur'+idx">
          <span class="metric-label">{{ item.label }}</span>
          <div class="metric-value-row">
            <span class="metric-value" :class="item.statusClass">{{ item.value }}</span>
            <span class="metric-unit">{{ item.unit }}</span>
          </div>
          <span class="metric-badge" :class="item.statusClass" v-if="item.status">{{ item.status }}</span>
        </div>
      </div>

      <div class="chart-group">
        <div class="chart-card" v-for="(chart, ci) in chartConfigs" :key="'chart'+ci">
          <div class="chart-card-title">{{ chart.title }}</div>
          <div class="chart-canvas" :id="'chart-canvas-' + ci" :style="{ width: '100%', height: '120px' }"></div>
        </div>
      </div>
    </div>

    <!-- 面板2: 右侧工程参数表格 -->
    <div class="info-panel">
      <div class="panel-header">
        <div>
          <div class="panel-kicker">工程参数</div>
          <h2 class="panel-title">{{ oneTitle }}</h2>
          <p class="panel-subtitle">特征水位 · 库容 · 防洪标准</p>
        </div>
        <button class="panel-close" @click="closeClick">×</button>
      </div>

      <div class="param-table">
        <div class="param-row param-row-header">
          <div class="param-cell param-cell-label">项目</div>
          <div class="param-cell param-cell-value">内容</div>
        </div>
        <div class="param-row" v-for="(item, index) in reservoirInfo" :key="index">
          <div class="param-cell param-cell-label">{{ item.label }}</div>
          <div class="param-cell param-cell-value">{{ item.value }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { findReservoirByName } from '@/services/reservoirCatalog.js'
import { generateReservoirTimeSeries } from '@/utils/reservoirTimeSeries.js'

export default {
  name: "regulatingReservoir",
  data() {
    return {
      oneTitle: "",
      reservoirInfo: [],
      currentMetrics: [],
      chartConfigs: [],
      tsData: null,
    };
  },
  created() {
    // 非 reactive 属性，不放 data() 里（Vue 2 中 _ 前缀属性不会被代理到 this）
    this.echartInstances = [];
  },
  props: {
    info: { type: Object, default: () => ({}) },
  },
  mounted() {
    this.updateData();
    this._resizeHandler = () => {
      this.echartInstances.forEach(c => { try { c.resize(); } catch(e) {} });
    };
    window.addEventListener('resize', this._resizeHandler);
  },
  watch: {
    info: {
      handler() { this.updateData(); },
      immediate: true,
    },
  },
  beforeDestroy() {
    this._destroyCharts();
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
  },
  methods: {
    updateData() {
      const reservoir = (this.info && this.info.reservoir) || findReservoirByName((this.info && this.info.name) || '薛峰');
      this.oneTitle = `${reservoir.name}信息`;
      const levels = reservoir.characteristic_levels || {};
      const snap = reservoir.snapshot || {};

      this.tsData = generateReservoirTimeSeries(reservoir);

      const currentLevel = snap.level;
      let levelStatus = '', statusClass = '';
      if (currentLevel != null) {
        if (currentLevel >= (levels.check_flood_level || 99999)) { levelStatus = '超校核'; statusClass = 'st-red'; }
        else if (currentLevel >= (levels.design_flood_level || 99999)) { levelStatus = '超设计洪水'; statusClass = 'st-red'; }
        else if (currentLevel >= (levels.normal_level || 99999)) { levelStatus = '正常高水位'; statusClass = 'st-green'; }
        else if (currentLevel >= (levels.flood_limit_level || 99999)) { levelStatus = '正常'; statusClass = 'st-green'; }
        else if (currentLevel >= (levels.dead_level || -99999)) { levelStatus = '偏低'; statusClass = 'st-yellow'; }
        else { levelStatus = '低于死水位'; statusClass = 'st-red'; }
      }
      const netFlow = (snap.inflow != null && snap.outflow != null) ? snap.inflow - snap.outflow : null;
      const totalCap = (reservoir.capacities || {}).total_capacity || 0;
      const deadL = levels.dead_level || 0;
      const normL = levels.normal_level || 1;
      const ratio = currentLevel != null ? Math.max(0, Math.min(1, (currentLevel - deadL) / (normL - deadL))) : 0;
      const curStorage = Math.round(totalCap * ratio * 1000) / 1000;

      this.currentMetrics = [
        { label: '库水位', value: currentLevel ?? '-', unit: 'm', status: levelStatus, statusClass },
        { label: '入库流量', value: snap.inflow ?? '-', unit: 'm³/s', status: '', statusClass: '' },
        { label: '出库流量', value: snap.outflow ?? '-', unit: 'm³/s', status: '', statusClass: '' },
        { label: '净流量', value: netFlow != null ? (netFlow > 0 ? '+' + netFlow : netFlow) : '-', unit: 'm³/s', status: netFlow > 0 ? '蓄水' : (netFlow < 0 ? '泄水' : ''), statusClass: netFlow > 0 ? 'st-green' : 'st-yellow' },
        { label: '降雨量', value: this.tsData ? this.tsData.rainfall[this.tsData.rainfall.length - 1] : '-', unit: 'mm', status: '', statusClass: '' },
        { label: '蓄水量', value: curStorage, unit: '亿 m³', status: '', statusClass: '' },
        { label: '气温', value: this.tsData ? this.tsData.temp[this.tsData.temp.length - 1] : '-', unit: '°C', status: '', statusClass: '' },
        { label: '湿度', value: this.tsData ? this.tsData.humidity[this.tsData.humidity.length - 1] : '-', unit: '%', status: '', statusClass: '' },
      ];

      const ts = this.tsData;
      this.chartConfigs = [
        { title: '库水位 (m)', data: ts.level, color: '#38bdf8', unit: 'm', markLine: { normal: levels.normal_level, floodLimit: levels.flood_limit_level, dead: levels.dead_level } },
        { title: '流量 (m³/s)', data: ts.inflow, data2: ts.outflow, color: '#34d399', color2: '#fbbf24', name1: '入库', name2: '出库', unit: 'm³/s' },
        { title: '降雨量 (mm)', data: ts.rainfall, color: '#60a5fa', unit: 'mm', area: true },
        { title: '蓄水量 (亿 m³)', data: ts.storage, color: '#a78bfa', unit: '亿 m³', area: true },
        { title: '气温 (°C)', data: ts.temp, color: '#fb7185', unit: '°C' },
        { title: '湿度 (%)', data: ts.humidity, color: '#22d3ee', unit: '%', area: true },
      ];

      this._buildInfoTable(reservoir);
      this.$nextTick(() => {
        setTimeout(() => this._renderCharts(), 300);
      });
    },

    _buildInfoTable(reservoir) {
      const location = (this.info && this.info.location) || reservoir.location || [];
      const coordinate = location.length >= 2 ? `${location[0]}°E，${location[1]}°N` : '演示规划点位';
      const levels = reservoir.characteristic_levels || {};
      const capacities = reservoir.capacities || {};
      const engineering = reservoir.engineering || {};
      const floodStandard = reservoir.flood_standard || {};

      const fmtCapacity = (v) => {
        if (v == null) return '—';
        return v >= 0.01 ? `${v} 亿 m³` : `${Math.round(v * 10000)} 万 m³`;
      };

      this.reservoirInfo = [
        { label: "水库类型", value: reservoir.reservoir_type || "调蓄水库" },
        { label: "所在河流", value: reservoir.river || '—' },
        { label: "主要功能", value: engineering.main_function || "城镇供水与农业灌溉调节" },
        { label: "总库容", value: fmtCapacity(capacities.total_capacity) },
        { label: "正常蓄水位", value: `${levels.normal_level} m` },
        { label: "死水位", value: `${levels.dead_level} m` },
        { label: "汛限水位", value: `${levels.flood_limit_level} m` },
        { label: "设计洪水位", value: `${levels.design_flood_level} m` },
        { label: "校核洪水位", value: `${levels.check_flood_level} m` },
        { label: "调节库容", value: fmtCapacity(capacities.regulation_capacity) },
      ];

      if (reservoir.catchment_area_km2 != null) {
        this.reservoirInfo.push({ label: "集雨面积", value: `${reservoir.catchment_area_km2} km²` });
      }
      this.reservoirInfo.push(
        { label: "设计洪水标准", value: floodStandard.design || "50 年一遇" },
        { label: "校核洪水标准", value: floodStandard.check || "100 年一遇" },
      );
      if (engineering.built_year != null) {
        this.reservoirInfo.push({ label: "建成年份", value: `${engineering.built_year} 年` });
      }
      this.reservoirInfo.push(
        { label: "坐标位置", value: coordinate },
        { label: "参数来源", value: reservoir.sourceNote },
        { label: "坐标说明", value: reservoir.coordinateNote || (this.info && this.info.source) || "配水工程调蓄设施公开坐标有限，当前采用演示规划坐标" },
      );
    },

    _renderCharts() {
      this._destroyCharts();
      if (!this.tsData || !this.chartConfigs.length) return;
      // 直接用 querySelectorAll 查找 canvas DOM，绕过 $refs 时序问题
      const containers = this.$el.querySelectorAll('.chart-canvas');
      console.log('[regulatingReservoir] _renderCharts: found', containers.length, 'canvas containers, chartConfigs:', this.chartConfigs.length);
      this.chartConfigs.forEach((cfg, idx) => {
        const el = containers[idx];
        if (!el) { console.warn('[regulatingReservoir] no container for chart', idx); return; }
        // 强制设置尺寸
        const w = el.offsetWidth || el.clientWidth || 380;
        const h = 120;
        el.style.width = w + 'px';
        el.style.height = h + 'px';
        console.log('[regulatingReservoir] init chart', idx, cfg.title, 'w=', w, 'h=', h, 'dataLen=', cfg.data ? cfg.data.length : 0);
        let chart;
        try {
          chart = echarts.init(el, null, { width: w, height: h, renderer: 'canvas' });
        } catch(e) {
          console.error('[regulatingReservoir] echarts.init error', idx, e);
          return;
        }
        this.echartInstances.push(chart);

        const series = [{
          name: cfg.name1 || cfg.title,
          type: 'line',
          data: cfg.data,
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 2, color: cfg.color },
          itemStyle: { color: cfg.color },
          areaStyle: cfg.area ? {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: cfg.color + '40' },
              { offset: 1, color: cfg.color + '05' },
            ]),
          } : undefined,
        }];

        if (cfg.data2) {
          series.push({
            name: cfg.name2,
            type: 'line',
            data: cfg.data2,
            smooth: true,
            symbol: 'none',
            lineStyle: { width: 2, color: cfg.color2 },
            itemStyle: { color: cfg.color2 },
          });
        }

        const markLines = [];
        if (cfg.markLine) {
          if (cfg.markLine.normal) markLines.push({ yAxis: cfg.markLine.normal, name: '正常蓄水位', lineStyle: { color: '#34d399', type: 'dashed', width: 1 }, label: { show: true, formatter: '正常', color: '#34d399', fontSize: 9 } });
          if (cfg.markLine.floodLimit) markLines.push({ yAxis: cfg.markLine.floodLimit, name: '汛限', lineStyle: { color: '#fbbf24', type: 'dashed', width: 1 }, label: { show: true, formatter: '汛限', color: '#fbbf24', fontSize: 9 } });
          if (cfg.markLine.dead) markLines.push({ yAxis: cfg.markLine.dead, name: '死水位', lineStyle: { color: '#94a3b8', type: 'dashed', width: 1 }, label: { show: true, formatter: '死水位', color: '#94a3b8', fontSize: 9 } });
        }

        chart.setOption({
          grid: { left: 50, right: 15, top: 25, bottom: 25 },
          tooltip: { trigger: 'axis', textStyle: { fontSize: 11 } },
          legend: cfg.data2 ? { show: true, data: [cfg.name1, cfg.name2], textStyle: { color: '#cbd5e1', fontSize: 10 }, top: 0, right: 5 } : { show: false },
          xAxis: {
            type: 'category',
            data: this.tsData.times,
            axisLabel: { color: '#94a3b8', fontSize: 9, interval: 23 },
            axisLine: { lineStyle: { color: 'rgba(148,163,184,0.3)' } },
          },
          yAxis: {
            type: 'value',
            scale: true,
            axisLabel: { color: '#94a3b8', fontSize: 9 },
            splitLine: { lineStyle: { color: 'rgba(148,163,184,0.1)' } },
          },
          series: series.map(s => {
            if (markLines.length > 0 && s === series[0]) {
              s.markLine = { silent: true, symbol: 'none', data: markLines };
            }
            return s;
          }),
        });
        // 延迟 resize 确保布局完成后图表自适应
        setTimeout(() => { try { chart.resize(); } catch(e) {} }, 100);
      });
    },

    _destroyCharts() {
      this.echartInstances.forEach(c => { try { c.dispose(); } catch(e) {} });
      this.echartInstances = [];
    },

    closeClick() { this.$emit("infoClose"); },
  },
};
</script>

<style scoped>
/* === 根容器：零尺寸锚点，子面板用 fixed 突破父级约束 === */
.reservoir-dual-panel {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 0 !important;
  height: 0 !important;
  z-index: 9999 !important;
  pointer-events: none !important;
  overflow: visible !important;
}

/* === 共用面板视觉（对齐演示导览 StorylineGuide 风格） === */
.ts-panel,
.info-panel {
  position: fixed !important;
  pointer-events: auto !important;
  z-index: 10000 !important;
  overflow-y: auto !important;
  color: #eef7ff !important;
  background: linear-gradient(135deg, rgba(8, 18, 32, 0.96), rgba(13, 38, 50, 0.94)) !important;
  border: 1px solid rgba(99, 179, 237, 0.28) !important;
  border-radius: 10px !important;
  box-shadow: 0 18px 52px rgba(0, 0, 0, 0.42) !important;
  box-sizing: border-box !important;
}

/* === 面板1: 左下角时间序列 === */
.ts-panel {
  left: 16px !important;
  bottom: 16px !important;
  top: auto !important;
  right: auto !important;
  width: 440px !important;
  max-width: 440px !important;
  max-height: calc(100vh - 120px) !important;
  padding: 18px !important;
}

/* === 面板2: 右侧工程参数 === */
.info-panel {
  right: 16px !important;
  top: 96px !important;
  left: auto !important;
  bottom: auto !important;
  width: clamp(360px, 30vw, 520px) !important;
  max-height: calc(100vh - 130px) !important;
  padding: 18px !important;
}

/* === 面板头部（对齐 StorylineGuide） === */
.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}
.panel-kicker {
  color: #76d9ff;
  font-size: 12px;
  margin-bottom: 6px;
  letter-spacing: 0.5px;
}
.panel-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #fff;
}
.panel-subtitle {
  margin: 8px 0 0;
  color: rgba(255, 255, 255, 0.64);
  font-size: 12px;
}
.panel-close {
  width: 30px;
  height: 30px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;
  transition: background 0.2s, border-color 0.2s;
}
.panel-close:hover {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.28);
}

/* === 实时指标卡片网格 === */
.metrics-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.metric-card {
  position: relative;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.08);
  min-width: 96px;
}
.metric-label {
  display: block;
  color: rgba(255, 255, 255, 0.52);
  font-size: 11px;
  margin-bottom: 4px;
}
.metric-value-row {
  display: flex;
  align-items: baseline;
  gap: 3px;
}
.metric-value {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
}
.metric-unit {
  color: rgba(125, 211, 252, 0.76);
  font-size: 10px;
}
.metric-badge {
  display: inline-block;
  height: 18px;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 10px;
  margin-top: 4px;
}

/* === 图表卡片 === */
.chart-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.chart-card {
  padding: 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.chart-card-title {
  color: rgba(125, 211, 252, 0.76);
  font-size: 11px;
  margin-bottom: 4px;
}
.chart-canvas {
  width: 100%;
  height: 110px;
  min-width: 300px;
  min-height: 110px;
}

/* === 参数表格（卡片风格） === */
.param-table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.param-row {
  display: flex;
  transition: background 0.2s;
}
.param-row:hover {
  background: rgba(56, 189, 248, 0.06);
}
.param-row-header {
  background: rgba(56, 189, 248, 0.1);
}
.param-row-header .param-cell {
  color: #76d9ff;
  font-weight: 700;
  font-size: 13px;
}
.param-row:not(.param-row-header) {
  background: rgba(255, 255, 255, 0.025);
}
.param-cell {
  flex: 1;
  padding: 9px 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.78);
}
.param-cell-label {
  flex: 0 0 38%;
  color: rgba(255, 255, 255, 0.58);
}
.param-cell-value {
  color: #eef7ff;
}

/* === 状态颜色 === */
.st-red { color: #fca5a5; background: rgba(252, 165, 165, 0.14); }
.st-yellow { color: #facc15; background: rgba(250, 204, 21, 0.12); }
.st-green { color: #86efac; background: rgba(134, 239, 172, 0.14); }
</style>
